import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { useParams, useSearchParams } from "react-router-dom";
import { getPublicTrack, Track } from "../data/schema";
import { ProgressSpinner } from "primereact/progressspinner";
import { showToast } from "components/ui-kit/Toast";
import LikeIcon from "../../../assets/like.svg";
import DislikeIcon from "../../../assets/dislike.svg";
import PlayIcon from "../../../assets/play-player.svg";
import MoreIcon from "../../../assets/more.svg";
import CommentIcon from "../../../assets/question_icon.svg";
import EditIcon from "../../../assets/edit-ws.svg";
import { SUNO_API_STORAGE_URL } from "constants/api";
import { MusicPlayer, MusicPlayerHandle } from "../RightPanel/MusicPlayer";
import { clearQueue } from "../data/queue";

export const Song: React.FC = () => {
  // UUID трека из URL или query-параметра
  const { uuid: uuidParam } = useParams<{ uuid?: string }>();
  const [searchParams] = useSearchParams();
  const trackUuid = uuidParam || searchParams.get('uuid');
  
  const [loading, setLoading] = useState(true);
  const [trackData, setTrackData] = useState<any>(null);
  const [reaction, setReaction] = useState<'like' | 'dislike' | null>(null);
  const [playerTrack, setPlayerTrack] = useState<Track | null>(null);
  const [playingTrack, setPlayingTrack] = useState<Track | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [sourceTracksForPlayer, setSourceTracksForPlayer] = useState<Track[]>([]);
  const playerRef = useRef<MusicPlayerHandle | null>(null);
  const hasPlayer = !!playingTrack;

  const buildTrackForPlayer = (apiTrack: any): Track => {
    const createdAtRaw = apiTrack.created_at ? new Date(apiTrack.created_at).getTime() : NaN;
    const safeCreatedAt = Number.isFinite(createdAtRaw) ? createdAtRaw : Date.now();

    let durationSec: number | undefined;
    if (typeof apiTrack.duration_sec === 'number') {
      durationSec = apiTrack.duration_sec;
    } else if (typeof apiTrack.duration_sec === 'string') {
      const parsed = parseFloat(apiTrack.duration_sec);
      durationSec = isNaN(parsed) ? undefined : parsed;
    }

    return {
      id: apiTrack.uuid || String(apiTrack.id),
      title: apiTrack.title || 'Song',
      createdAt: safeCreatedAt,
      description: apiTrack.description || undefined,
      params: {
        uuid: apiTrack.uuid,
        sunoTrackId: apiTrack.id,
        sunoAudioId: apiTrack.suno_audio_id,
        localAudioUrl: apiTrack.local_audio_url,
        audioUrl: apiTrack.local_audio_url,
        localImageUrl: apiTrack.local_image_url,
        imageUrl: apiTrack.local_image_url,
        modelName: apiTrack.model_name,
        tags: apiTrack.tags,
        sunoRequest: apiTrack.params || undefined,
      },
      durationSec,
      reaction: apiTrack.reaction || null,
      hasPersonas: apiTrack.has_personas ?? false,
      fullSongs: apiTrack.full_songs ?? true,
      trashed: apiTrack.trashed ?? false,
    };
  };

  useEffect(() => {
    if (!trackUuid) {
      showToast('Track UUID is required', undefined, 'error');
      setLoading(false);
      return;
    }

    const fetchTrackData = async () => {
      try {
        setLoading(true);
        const response = await getPublicTrack(trackUuid);

        if (!response?.data) {
          showToast('Track not found', undefined, 'error');
          setLoading(false);
          return;
        }

        const track = response.data;
        setTrackData(track);

        // реакция, если сервер возвращает
        if (track.reaction) {
          setReaction(
            track.reaction === 'like'
              ? 'like'
              : track.reaction === 'dislike'
              ? 'dislike'
              : null
          );
        }

        // Подготовка трека для MusicPlayer
        const builtTrack = buildTrackForPlayer(track);
        setPlayerTrack(builtTrack);
        setSourceTracksForPlayer([builtTrack]);
      } catch (error) {
        console.error('Failed to fetch track data:', error);
        showToast('Failed to load track', undefined, 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchTrackData();
  }, [trackUuid]);

  if (loading) {
    return (
      <Container>
        <Loader>
          <ProgressSpinner />
        </Loader>
      </Container>
    );
  }

  if (!trackData) {
    return (
      <Container>
        <ErrorMessage>Track not found</ErrorMessage>
      </Container>
    );
  }

  // Use fields from the public API response structure
  // local_image_url может приходить уже как полный URL — в этом случае не добавляем префикс
  const rawImage = trackData.local_image_url;
  const imageUrl =
    typeof rawImage === 'string' && rawImage.length > 0
      ? (rawImage.startsWith('http://') || rawImage.startsWith('https://')
          ? rawImage
          : `${SUNO_API_STORAGE_URL}/${rawImage}`)
      : null;
  
  const title = trackData.title || 'Untitled';
  const description: string = (trackData.description || '').toString();

  // Имя пользователя — пробуем несколько возможных полей
  const username =
    trackData.user?.username ||
    trackData.user?.name ||
    trackData.username ||
    trackData.user_name ||
    'Unknown User';

  // Отображаемый никнейм (если есть)
  const nickname =
    trackData.user?.nickname ||
    trackData.nickname ||
    trackData.user_nickname ||
    null;

  // Аватар пользователя
  const rawAvatar =
    trackData.user?.avatar_url ||
    trackData.user?.avatar ||
    trackData.user?.avatarUrl ||
    trackData.avatar_url ||
    trackData.avatarUrl ||
    trackData.user_avatar ||
    trackData.avatar ||
    null;

  const avatarUrl =
    typeof rawAvatar === 'string' && rawAvatar.length > 0
      ? (rawAvatar.startsWith('http://') || rawAvatar.startsWith('https://')
          ? rawAvatar
          : `${SUNO_API_STORAGE_URL}/${rawAvatar}`)
      : null;

  // Дата создания — в приоритете created_at
  const createdAt = trackData.created_at || trackData.suno_created_at;

  // Название модели — из params.model, затем fallback
  const model =
    (trackData.params && typeof trackData.params.model === 'string' && trackData.params.model) ||
    trackData.model_name ||
    'V4_5';
  
  // Format date
  let formattedDate = '';
  if (createdAt) {
    try {
      const date = new Date(createdAt);
      formattedDate = date.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (e) {
      formattedDate = createdAt;
    }
  }

  const handlePlay = () => {
    if (!playerTrack) {
      showToast('Audio is not available yet', undefined, 'error');
      return;
    }

    // Если уже открыт плеер с этим треком — просто переключаем паузу
    if (playingTrack && playingTrack.id === playerTrack.id && playerRef.current) {
      playerRef.current.togglePlay();
      return;
    }

    // Инициализируем очередь только с этим треком и открываем MusicPlayer
    clearQueue();
    setSourceTracksForPlayer([playerTrack]);
    setPlayingTrack(playerTrack);
  };

  const handleComment = () => {
    // TODO: Implement comment functionality
    console.log('Comment on track:', trackData);
  };

  const handleLike = () => {
    const next = reaction === 'like' ? null : 'like';
    setReaction(next);
    // TODO: Implement like functionality
    console.log('Like track:', trackData, next);
  };

  const handleDislike = () => {
    const next = reaction === 'dislike' ? null : 'dislike';
    setReaction(next);
    // TODO: Implement dislike functionality
    console.log('Dislike track:', trackData, next);
  };

  const handleMore = () => {
    // TODO: Implement more options functionality
    console.log('More options for track:', trackData);
  };

  const handleEditLyrics = () => {
    // TODO: Implement edit lyrics functionality
    console.log('Edit lyrics for track:', trackData);
  };

  return (
    <Container $hasPlayer={hasPlayer}>
      <Content>
        <LeftSection>
          <Artwork>
            <ArtworkImage $imageUrl={imageUrl} />
          </Artwork>
          <ActionButtons>
            <ActionButton onClick={handlePlay} title="Play">
              <ActionIcon>
                <PlayIcon />
              </ActionIcon>
            </ActionButton>
            
            <ActionButton 
              onClick={handleLike} 
              title="Like"
              $active={reaction === 'like'}
            >
              <ActionIcon $active={reaction === 'like'}>
                <LikeIcon />
              </ActionIcon>
            </ActionButton>
            <ActionButton 
              onClick={handleDislike} 
              title="Dislike"
              $active={reaction === 'dislike'}
            >
              <ActionIcon $active={reaction === 'dislike'}>
                <DislikeIcon />
              </ActionIcon>
            </ActionButton>
          </ActionButtons>
          {/* <EditLyricsButton onClick={handleEditLyrics}>
            <EditIconWrapper>
              <EditIcon />
            </EditIconWrapper>
            <EditLyricsText>Edit Displayed Lyrics</EditLyricsText>
          </EditLyricsButton> */}
        </LeftSection>

        <RightSection>
          <Title>{title}</Title>
         
          
          <UserInfo>
            <Avatar $avatarUrl={avatarUrl} />
            <Username>{nickname || username}</Username>
          </UserInfo>

          <DateInfo>
            <DateText>{formattedDate}</DateText>
            <VersionTag>{model}</VersionTag>
          </DateInfo>

          {description && (
            <DescriptionBlock>
              <DescriptionTitle>Description</DescriptionTitle>
              <DescriptionText>{description}</DescriptionText>
            </DescriptionBlock>
          )}

        </RightSection>
      </Content>
      {playingTrack && (
        <MusicPlayer
          ref={playerRef}
          track={playingTrack}
          onClose={() => {
            setPlayingTrack(null);
            setIsPlaying(false);
            setSourceTracksForPlayer([]);
          }}
          onTrackChange={(newTrack) => setPlayingTrack(newTrack)}
          onTogglePlay={setIsPlaying}
          sourceTracks={sourceTracksForPlayer}
        />
      )}
    </Container>
  );
};

const Container = styled.div<{ $hasPlayer?: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  background: #fff;
  padding: 40px;
  padding-bottom: ${p => (p.$hasPlayer ? '120px' : '40px')};
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  overflow: hidden;
  transition: padding-bottom 0.3s ease;
`;

const Loader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100vh;
`;

const ErrorMessage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100vh;
  color: #666;
  font-size: 16px;
`;

const Content = styled.div`
  display: flex;
  gap: 60px;
  max-width: 1200px;
  width: 100%;
  align-items: flex-start;
  height: 100%;
  overflow: hidden;
`;

const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 300px;
`;

const Artwork = styled.div`
  width: 100%;
  max-width: 400px;
  aspect-ratio: 3 / 4;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const ArtworkImage = styled.div<{ $imageUrl?: string | null }>`
  width: 100%;
  height: 100%;
  background: ${({ $imageUrl }) =>
    $imageUrl
      ? `url(${$imageUrl}) center center / cover no-repeat`
      : 'linear-gradient(135deg, #e8eaff, #f4f4f4)'};
`;

const TrackName = styled.div`
  font-size: 18px;
  font-weight: 500;
  color: #333;
  margin-bottom: 16px;
  text-align: left;
`;

const EditLyricsButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #e8e8e8;
    border-color: #d0d0d0;
  }
`;

const EditIconWrapper = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const DescriptionBlock = styled.div`
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DescriptionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #555;
`;

const DescriptionText = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: #444;
  white-space: pre-wrap;
`;

const EditLyricsText = styled.span`
  font-size: 14px;
  color: #666;
  font-weight: 400;
`;

const RightSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: 100%;
  max-height: 100%;
  overflow-y: auto;
  padding-right: 12px;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 600;
  color: #333;
  margin: 0;
  font-family: serif;
  line-height: 1.2;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: #e0e0e0;
  margin: 8px 0;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Avatar = styled.div<{ $avatarUrl?: string | null }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  position: relative;
  background: ${({ $avatarUrl }) =>
    $avatarUrl
      ? `url(${$avatarUrl}) center center / cover no-repeat`
      : 'linear-gradient(135deg, #ff6b9d, #c44569, #ff8c42)'};
`;

const Username = styled.div`
  font-size: 14px;
  color: #666;
  font-weight: 400;
`;

const DateInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
`;

const DateText = styled.div`
  font-size: 14px;
  color: #666;
`;

const VersionTag = styled.div`
  padding: 4px 12px;
  background: #f0f0f0;
  border-radius: 12px;
  font-size: 12px;
  color: #666;
  font-weight: 500;
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
  justify-content: center;
  width: 100%;
`;

const ActionButton = styled.button<{ $active?: boolean }>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${p => p.$active ? '#6777FF' : '#f5f5f5'};
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${p => p.$active ? '#5566ee' : '#e8e8e8'};
    transform: scale(1.05);
  }
`;

const ActionIcon = styled.div<{ $active?: boolean }>`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${p => p.$active ? 1 : 0.7};
  
  svg {
    width: 100%;
    height: 100%;
    filter: ${p => p.$active ? 'brightness(0) invert(1)' : 'none'};
  }
  
  svg,
  svg * {
    fill: currentColor;
  }
`;
